import { useState } from 'react';
import { DatePicker, Segmented } from 'antd';
import dayjs from 'dayjs';
import { useLazyExportSalesXlsxQuery, useSalesReportQuery } from '@/entities/reports';
import type {
  SalesGroupBy,
  SalesReportRow,
  SalesReportRowCategory,
  SalesReportRowCounterparty,
  SalesReportRowDay,
  SalesReportRowDocument,
} from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';
import { formatMoney, sumMoney } from '@/shared/lib/money';

const GROUP_OPTIONS: { value: SalesGroupBy; label: string }[] = [
  { value: 'day', label: 'По дням' },
  { value: 'category', label: 'По товарам' },
  { value: 'counterparty', label: 'По клиентам' },
  { value: 'document', label: 'По документам' },
];

const money = (v: string) => formatMoney(v);

const DOCUMENT_COLUMNS: ResponsiveColumns<SalesReportRowDocument> = [
  { title: 'Номер', dataIndex: 'number', width: 110 },
  { title: 'Дата', dataIndex: 'docDate', width: 110, mobile: 'subtitle', render: (v: string) => formatDate(v) },
  { title: 'Контрагент', dataIndex: 'counterpartyName', render: (v: string | null) => v ?? '—' },
  { title: 'Сумма', dataIndex: 'totalAmount', width: 120, align: 'right', render: money },
];
const CATEGORY_COLUMNS: ResponsiveColumns<SalesReportRowCategory> = [
  { title: 'Товар', dataIndex: 'categoryName' },
  { title: 'Штук', dataIndex: 'qty', width: 100, align: 'right' },
  { title: 'Сумма', dataIndex: 'amount', width: 120, align: 'right', render: money },
];
const COUNTERPARTY_COLUMNS: ResponsiveColumns<SalesReportRowCounterparty> = [
  { title: 'Контрагент', dataIndex: 'counterpartyName' },
  { title: 'Документов', dataIndex: 'docCount', width: 110, align: 'right' },
  { title: 'Сумма', dataIndex: 'amount', width: 120, align: 'right', render: money },
];
const DAY_COLUMNS: ResponsiveColumns<SalesReportRowDay> = [
  { title: 'Дата', dataIndex: 'docDate', width: 110, render: (v: string) => formatDate(v) },
  { title: 'Документов', dataIndex: 'docCount', width: 110, align: 'right' },
  { title: 'Сумма', dataIndex: 'amount', width: 120, align: 'right', render: money },
];

const COLUMNS_BY_GROUP: Record<SalesGroupBy, ResponsiveColumns<SalesReportRow>> = {
  document: DOCUMENT_COLUMNS as ResponsiveColumns<SalesReportRow>,
  category: CATEGORY_COLUMNS as ResponsiveColumns<SalesReportRow>,
  counterparty: COUNTERPARTY_COLUMNS as ResponsiveColumns<SalesReportRow>,
  day: DAY_COLUMNS as ResponsiveColumns<SalesReportRow>,
};

/** Денежный столбец режима: по документам — `totalAmount`, в остальных — `amount`. */
const AMOUNT_FIELD: Record<SalesGroupBy, 'totalAmount' | 'amount'> = { document: 'totalAmount', category: 'amount', counterparty: 'amount', day: 'amount' };

function rowKeyFor(groupBy: SalesGroupBy, row: SalesReportRow): string {
  if (groupBy === 'document') return (row as SalesReportRowDocument).documentId;
  if (groupBy === 'category') return (row as SalesReportRowCategory).categoryId;
  if (groupBy === 'counterparty') return (row as SalesReportRowCounterparty).counterpartyId;
  return (row as SalesReportRowDay).docDate;
}

/** §13: продажи — по документам/товарам/клиентам/дням. */
export function SalesReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultFrom = dayjs(today).startOf('month').format('YYYY-MM-DD');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const [groupBy, setGroupBy] = useState<SalesGroupBy>('day');
  const periodChanged = from !== defaultFrom || to !== today;
  const { data, isFetching } = useSalesReportQuery({ from, to, groupBy }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportSalesXlsxQuery();
  const rows = data?.rows ?? [];
  const amountField = AMOUNT_FIELD[groupBy];
  const totals = rows.length > 0 ? { cells: { [amountField]: formatMoney(sumMoney(rows.map((r) => (r as unknown as Record<string, string>)[amountField]))) } } : undefined;

  return (
    <ReportPage
      title="Продажи"
      filters={
        <>
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            inputReadOnly={isMobile}
            value={[dayjs(from), dayjs(to)]}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFrom(dates[0].format('YYYY-MM-DD'));
                setTo(dates[1].format('YYYY-MM-DD'));
              }
            }}
          />
          <Segmented value={groupBy} onChange={(v) => setGroupBy(v)} options={GROUP_OPTIONS} vertical={isMobile} />
        </>
      }
      columns={COLUMNS_BY_GROUP[groupBy]}
      activeFilters={(periodChanged ? 1 : 0) + (groupBy !== 'day' ? 1 : 0)}
      onResetFilters={() => {
        setFrom(defaultFrom);
        setTo(today);
        setGroupBy('day');
      }}
      filtersSummary={`${formatDate(from)} — ${formatDate(to)} · ${GROUP_OPTIONS.find((o) => o.value === groupBy)?.label ?? ''}`}
      data={rows}
      totals={totals}
      loading={isFetching}
      rowKey={(r) => rowKeyFor(groupBy, r)}
      onExport={() => exportXlsx({ from, to, groupBy }).unwrap()}
      exportFilename="Продажи.xlsx"
    />
  );
}
